import Context from "shared/util/Context";
import $ from "jquery";
import _str from "shared/util/_str";
import * as ProfileActions from "shared/util/ProfileActions";
import {
	OKEVENT,
	subscribeToOkEvent,
	unsubscribeFromOkEvent,
} from "shared/util/OkEventHelpers";

import Web from "apps/profile_questions/util/web";
import {
	USER_TYPES,
	FILTERS,
	ANSWERED_ALL_QUESTIONS_KEY,
} from "apps/profile_questions/util/constants";

import {
	getSelfviewQuestionCategories,
	mapQuestionList,
	updateMobileWebMatchPercentage,
	getFilterUrlParam,
} from "apps/profile_questions/util/helpers";

import {
	INITIAL_STATE as questionsInitialState,
	FETCH_QUESTION_SUCCESS,
	SUBMIT_ANSWER_SUCCESS,
} from "ducks/questions";


// initial state
// ----------------------------------------------------------------------------

const INITIAL_STATE = {
	questions: undefined,
	nextQuestion: undefined,
	nextQuestionError: undefined,
	filters: undefined,
	activeFilter: getFilterUrlParam(),
	activeSort: undefined,
	paging: undefined,
	isLiking: false,
	isSelfView: false,
	isGettingFilters: false,
	isGettingUserInfo: false,
	isGettingQuestions: false,
	isGettingNextQuestion: false,
	lastQuestionFetchFilter: undefined,
	error: undefined,
	answeringQid: undefined,
	[USER_TYPES.TARGET]: {},
	[USER_TYPES.VIEWER]: {},
};


// action types
// ----------------------------------------------------------------------------

export const SET_SELF_VIEW = "PROFILE_QUESTIONS.SET_SELF_VIEW";
export const SET_ACTIVE_FILTER = "PROFILE_QUESTIONS.SET_ACTIVE_FILTER";
export const SET_ACTIVE_SORT = "PROFILE_QUESTIONS.SET_ACTIVE_SORT";
export const CLEAR_QUESTIONS = "PROFILE_QUESTIONS.CLEAR_QUESTIONS";

export const GET_USER_INFO = "PROFILE_QUESTIONS.GET_USER_INFO";
export const GET_USER_INFO_SUCCESS = "PROFILE_QUESTIONS.GET_USER_INFO_SUCCESS";
export const GET_USER_INFO_FAILURE = "PROFILE_QUESTIONS.GET_USER_INFO_FAILURE";

export const GET_FILTERS = "PROFILE_QUESTIONS.GET_FILTERS";
export const GET_FILTERS_SUCCESS = "PROFILE_QUESTIONS.GET_FILTERS_SUCCESS";
export const GET_FILTERS_FAILURE = "PROFILE_QUESTIONS.GET_FILTERS_FAILURE";

export const GET_QUESTIONS = "PROFILE_QUESTIONS.GET_QUESTIONS";
export const GET_QUESTIONS_SUCCESS = "PROFILE_QUESTIONS.GET_QUESTIONS_SUCCESS";
export const GET_QUESTIONS_FAILURE = "PROFILE_QUESTIONS.GET_QUESTIONS_FAILURE";

export const GET_NEXT_QUESTION = "PROFILE_QUESTIONS.GET_NEXT_QUESTION";
export const GET_NEXT_QUESTION_SUCCESS = "PROFILE_QUESTIONS.GET_NEXT_QUESTION_SUCCESS";
export const GET_NEXT_QUESTION_FAILURE = "PROFILE_QUESTIONS.GET_NEXT_QUESTION_FAILURE";

export const SKIP_NEXT_QUESTION = "PROFILE_QUESTIONS.SKIP_NEXT_QUESTION";
export const SKIP_NEXT_QUESTION_SUCCESS = "PROFILE_QUESTIONS.SKIP_NEXT_QUESTION_SUCCESS";
export const SKIP_NEXT_QUESTION_FAILURE = "PROFILE_QUESTIONS.SKIP_NEXT_QUESTION_FAILURE";

export const LIKE = "PROFILE_QUESTIONS.LIKE";
export const LIKE_SUCCESS = "PROFILE_QUESTIONS.LIKE_SUCCESS";
export const LIKE_FAILURE = "PROFILE_QUESTIONS.LIKE_FAILURE";

export const ANSWER_NEXT_QUESTION = "PROFILE_QUESTIONS.ANSWER_NEXT_QUESTION";
export const SET_ANSWERING_QUESTION = "PROFILE_QUESTIONS.SET_ANSWERING_QUESTION";
export const CLEAR_ANSWERING_QUESTION = "PROFILE_QUESTIONS.CLEAR_ANSWERING_QUESTION";

export const ADMIN_REANSWER = "PROFILE_QUESTIONS.ADMIN_REANSWER";


// reducer
// ----------------------------------------------------------------------------

export function reducer(state = INITIAL_STATE, action) {
	switch (action.type) {
		case SET_SELF_VIEW: {
			return {
				...state,
				isSelfView: action.payload.isSelfView,
			};
		}

		case SET_ACTIVE_FILTER: {
			return {
				...state,
				activeFilter: action.payload.filter,
			};
		}

		case SET_ACTIVE_SORT: {
			return {
				...state,
				activeSort: action.payload.sort,
			};
		}

		case GET_USER_INFO: {
			return {
				...state,
				isGettingUserInfo: true,
			};
		}

		case GET_USER_INFO_SUCCESS: {
			return {
				...state,
				[action.payload.userType]: {
					...action.payload.userInfo,
				},
				isGettingUserInfo: false,
			};
		}

		case GET_USER_INFO_FAILURE: {
			return {
				...state,
				error: action.payload.error,
				isGettingUserInfo: false,
			};
		}

		case GET_FILTERS: {
			return {
				...state,
				isGettingFilters: true,
			};
		}

		case GET_FILTERS_SUCCESS: {
			return {
				...state,
				isGettingFilters: false,
				filters: action.payload.filters,
			};
		}

		case GET_FILTERS_FAILURE: {
			return {
				...state,
				isGettingFilters: false,
				error: action.payload.error,
			};
		}

		case GET_QUESTIONS: {
			return {
				...state,
				isGettingQuestions: true,
				lastQuestionFetchFilter: action.payload.lastQuestionFetchFilter,
			};
		}

		case GET_QUESTIONS_SUCCESS: {
			const questions = state.questions || [];

			if (action.payload.filter !== state.lastQuestionFetchFilter) {
				return { ...state };
			}

			// add questions, then filter them by qid
			const unfilteredQuestions = [
				...questions,
				...action.payload.questions,
			];
			const qidMap = {};
			const questionsRes = [];

			unfilteredQuestions.forEach((q) => {
				if (!qidMap[q.question.id]) {
					questionsRes.push(q);
					qidMap[q.question.id] = true;
				}
			});

			return {
				...state,
				isGettingQuestions: false,
				error: undefined,
				questions: questionsRes,
				paging: action.payload.paging,
			};
		}

		case GET_QUESTIONS_FAILURE: {
			return {
				...state,
				isGettingQuestions: false,
				error: action.payload.error,
			};
		}

		case CLEAR_QUESTIONS: {
			return {
				...state,
				questions: [],
				paging: {},
			};
		}

		case GET_NEXT_QUESTION:
		case SKIP_NEXT_QUESTION: {
			return {
				...state,
				isGettingNextQuestion: true,
				nextQuestionError: null,
			};
		}

		case GET_NEXT_QUESTION_SUCCESS: {
			return {
				...state,
				isGettingNextQuestion: false,
				nextQuestion: action.payload.question,
			};
		}

		case GET_NEXT_QUESTION_FAILURE: {
			return {
				...state,
				isGettingNextQuestion: false,
				nextQuestionError: action.payload.error,
			};
		}

		case SKIP_NEXT_QUESTION_SUCCESS: {
			return {
				...state,
				nextQuestion: null,
				isGettingNextQuestion: false,
			};
		}

		case SKIP_NEXT_QUESTION_FAILURE: {
			return {
				...state,
				isGettingNextQuestion: false,
				nextQuestionError: action.payload.error,
			};
		}

		case LIKE: {
			return {
				...state,
				isLiking: true,
			};
		}

		case LIKE_SUCCESS: {
			return {
				...state,
				isLiking: false,
				targetUser: {
					...state.targetUser,
					likes: {
						...state.targetUser.likes,
						you_like: 1,
					},
				},
			};
		}

		case LIKE_FAILURE: {
			return {
				...state,
				isLiking: false,
				error: action.payload.error,
			};
		}

		case ANSWER_NEXT_QUESTION: {
			const nextQid = state.nextQuestion ? state.nextQuestion.id : null;

			return {
				...state,
				answeringQid: nextQid,
			};
		}

		case SET_ANSWERING_QUESTION: {
			return {
				...state,
				answeringQid: action.payload.qid,
			};
		}

		case CLEAR_ANSWERING_QUESTION: {
			return {
				...state,
				answeringQid: undefined,
			};
		}

		// when the user answers a question, hook into the success to update the
		// question, and in selfview their answer count as well
		case SUBMIT_ANSWER_SUCCESS: {
			if (state.isSelfView) {
				let targetUser = { ...state[USER_TYPES.TARGET] };
				let questions = [ ...state.questions ];
				let filters = [ ...state.filters ];

				const questionIsInList = !!questions.find((q) => {
					return q.question.id === action.payload.question.question.id;
				});

				const isNewAnswer = !questionIsInList &&
					action.payload.num_questions &&
					state[USER_TYPES.TARGET].question_summary &&
					state[USER_TYPES.TARGET].question_summary.num_questions &&
					state[USER_TYPES.TARGET].question_summary.num_questions !== action.payload.num_questions;

				// update the user's answer count
				if (action.payload.num_questions) {
					targetUser = {
						...state[USER_TYPES.TARGET],
						question_summary: {
							...state[USER_TYPES.TARGET].question_summary,
							num_questions: action.payload.num_questions,
						}
					};
				}

				// update the question list and filter counts
				if (action.payload.question && isNewAnswer) {
					const questionCategories = getSelfviewQuestionCategories(
						action.payload.question
					);

					// add the question to the top of the questions list if we're showing
					// its category
					if (questionCategories.indexOf(state.activeFilter) >= 0) {
						questions = [
							action.payload.question,
							...questions,
						];
					}

					// update the filter counts
					filters = filters.map((_filter) => {
						const filter = { ..._filter };

						if (questionCategories.indexOf(filter.filter_id) >= 0) {
							filter.count += 1;
						}

						return filter;
					});
				} else if (action.payload.question && !isNewAnswer) {
					questions = mapQuestionList(state.questions, action.payload.question);
				}

				return {
					...state,
					questions,
					filters,
					[USER_TYPES.TARGET]: targetUser,
				};
			} else {
				const questions = mapQuestionList(state.questions, action.payload.question);

				return {
					...state,
					questions,
				};
			}
		}

		case ADMIN_REANSWER: {
			const questions = state.questions.map((_q) => {
				const q = { ..._q };

				if (q.question.id === action.payload.qid) {
					q.question.adminReanswer = true;
				}

				return q;
			});

			return {
				...state,
				questions,
			};
		}

		default: {
			return state;
		}
	}
}


// actions
// ----------------------------------------------------------------------------

/*
 * Handle the response of a user info call. If the returned userid matches
 * Context and the user type is "target", this means the we are in self view,
 * so we can save this info as the viewing user, too. If not, then fire off a
 * fetch for getting info for the viewer.
 *
 * @param {Function} dispatch - a redux dispatcher
 * @param {Function} getUserInfo - the user fetch action
 * @param {Object} res - an object of userinfo
 * @param {string} userType - if we are fetching info for the target or viewer
 */
const handleUserInfoResponse = (dispatch, getUserInfo, res, userType) => {
	// update the mobile web match score display. it's just like react! only...
	// worse
	if (
		userType === USER_TYPES.TARGET &&
		res &&
		res.percentages
	) {
		updateMobileWebMatchPercentage(res.percentages.match);
	}

	if (res.userid === Context.get("userid")) {
		if (userType === USER_TYPES.TARGET) {
			dispatch({
				type: SET_SELF_VIEW,
				payload: {
					isSelfView: true,
				},
			});

			dispatch({
				type: GET_USER_INFO_SUCCESS,
				payload: {
					userInfo: res,
					userType: USER_TYPES.VIEWER,
				},
			});
		}
	} else {
		dispatch(getUserInfo(Context.get("userid"), USER_TYPES.VIEWER));
	}
};

/*
 * This function gets user info and saves it to the store. If we are on another
 * person's profile, this function calls itself to get user info for the
 * viewing user as well.
 *
 * The reason for this complexity is that the username that we get from the url
 * isn't necessarily unique, due to username aliasing. So we can't just look at
 * the username from the url and compare it to Context to know if we are on
 * self view, we need to ask the server and compare userids.
 *
 * @param {string} userid - the userid or username to look up
 * @param {string} [userType] - a flag indicating if this is for the viewer or
 *   target user
 */
export const getUserInfo = (userid, userType = USER_TYPES.TARGET) => {
	return (dispatch) => {
		dispatch({ type: GET_USER_INFO });

		Web.getUserInfo(userid)
			.then((res) => {
				// save the results to the store
				dispatch({
					type: GET_USER_INFO_SUCCESS,
					payload: {
						userInfo: res,
						userType: userType,
					},
				});

				// possibly call this function again for the other user
				handleUserInfoResponse(dispatch, getUserInfo, res, userType);
			}).catch(() => {
				dispatch({
					type: GET_USER_INFO_FAILURE,
					error: true,
					payload: {
						error: _str("There was a problem getting information about this person!"),
					},
				});
			});
	};
};

export const setSelfview = (isSelfview) => {
	return {
		type: SET_SELF_VIEW,
		payload: {
			isSelfView: isSelfview,
		},
	};
};

export const getFilters = (userid) => {
	return (dispatch, getStore) => {
		const { profileQuestions } = getStore();
		const isFirstFiltersLoad = !profileQuestions.filters;
		dispatch({ type: GET_FILTERS });

		Web.getFilters(userid).
			then((res) => {
				dispatch({
					type: GET_FILTERS_SUCCESS,
					payload: {
						filters: res,
					}
				});

				// set the first filter as active if this is the first time this page
				// load that we're fetching the filters
				if (
					isFirstFiltersLoad &&
					res.length &&
					typeof res[0].filter_id !== "undefined"
				) {
					const filterParam = getFilterUrlParam();

					dispatch({
						type: SET_ACTIVE_FILTER,
						payload: {
							filter: filterParam || res[0].filter_id,
						},
					});
				}
			}).catch((err) => {
				console.error(err);

				dispatch({
					type: GET_FILTERS_FAILURE,
					error: true,
					payload: {
						error: _str("We had trouble loading these question filters!"),
					}
				});
			});
	};
};

/*
 * Get a list of questions for the current user and currently active filter.
 *
 * @param {Object} options - a dictionary of options
 * @param {boolean} [options.resetPagination] - reset the pagination cursors
 *   and fetch the first page
 */
export const getQuestions = (options = {}) => {
	const genericError = _str("There was a problem getting questions! Refresh and try again.");

	return (dispatch, getStore) => {
		const { profileQuestions } = getStore();
		const userid = profileQuestions.targetUser.userid;

		if (!userid) { return; }

		dispatch({
			type: GET_QUESTIONS,
			payload: {
				lastQuestionFetchFilter: profileQuestions.activeFilter,
			},
		});

		const params = {
			after: profileQuestions.paging && profileQuestions.paging.cursors ?
				profileQuestions.paging.cursors.after :
				null,
			filter: profileQuestions.activeFilter,
			sort: profileQuestions.activeSort,
		};

		if (options.resetPagination) {
			params.after = null;
		}

		Web.getQuestions(userid, params)
			.then((res) => {
				if (res.data && Array.isArray(res.data)) {
					if (options.resetPagination) {
						dispatch({ type: CLEAR_QUESTIONS });
					}

					dispatch({
						type: GET_QUESTIONS_SUCCESS,
						payload: {
							questions: res.data,
							filter: params.filter,
							paging: res.paging,
						},
					});
				} else {
					console.error(res);

					dispatch({
						type: GET_QUESTIONS_FAILURE,
						error: true,
						payload: {
							error: genericError,
						}
					});
				}
			}).catch((err) => {
				console.error(err);
				let message = genericError;

				if (err.error && err.error.message) {
					message = err.error.message;
				}

				dispatch({
					type: GET_QUESTIONS_FAILURE,
					error: true,
					payload: {
						error: message,
					}
				});
			});
	};
};

export const clearQuestions = () => {
	return { type: CLEAR_QUESTIONS };
};

export const setActiveFilter = (filter) => {
	return (dispatch) => {
		dispatch({
			type: SET_ACTIVE_FILTER,
			payload: {
				filter,
			},
		});

		dispatch({ type: CLEAR_QUESTIONS });
		dispatch(getQuestions());
	};
};

export const setActiveSort = (sort) => {
	return (dispatch) => {
		dispatch({
			type: SET_ACTIVE_SORT,
			payload: {
				sort,
			},
		});

		dispatch({ type: CLEAR_QUESTIONS });
		dispatch(getQuestions());
	};
};

export const getNextQuestion = () => {
	const error = _str("We couldn’t load the next question for you to answer; refresh and try again!");

	return (dispatch) => {
		dispatch({ type: GET_NEXT_QUESTION });

		Web.getNextQuestion()
			.then((res) => {
				dispatch({
					type: GET_NEXT_QUESTION_SUCCESS,
					payload: {
						question: res,
					},
				});
			}).catch((err) => {
				// catch the no more questions "error" to show a completion state
				if (
					typeof err === "object" &&
					err.error && err.error.type && err.error.type === "NoResults"
				) {
					dispatch({
						type: GET_NEXT_QUESTION_SUCCESS,
						payload: {
							question: {
								[ANSWERED_ALL_QUESTIONS_KEY]: true,
							},
						},
					});
				} else {
					let message = error;

					if (err && err.error && err.error.message) {
						message = `${_str("There was a problem loading the next question")}: ${err.error.message}`;
					}

					dispatch({
						type: GET_NEXT_QUESTION_FAILURE,
						error: true,
						payload: {
							error: message,
						},
					});
				}
			});
	};
};

export const skipQuestion = (qid) => {
	const error = _str("We couldn’t skip that question; refresh and try again!");

	return (dispatch, getStore) => {
		dispatch({ type: SKIP_NEXT_QUESTION });

		const store = getStore().profileQuestions;

		Web.skipQuestion(qid)
			.then((res) => {
				if (res.success) {
					dispatch({ type: SKIP_NEXT_QUESTION_SUCCESS });

					// now that we've skipped, fetch the next question, update the
					// sidebar counts, and get the new questions list if we are in the
					// skip category
					dispatch(getNextQuestion());
					dispatch(getFilters(store[USER_TYPES.TARGET].userid));

					if (store.activeFilter === FILTERS.SKIPPED) {
						dispatch(getQuestions({ resetPagination: true }));
					}
				} else {
					dispatch({
						type: SKIP_NEXT_QUESTION_FAILURE,
						error: true,
						payload: {
							error,
						},
					});
				}
			}).catch((err) => {
				console.error(err);

				let message = error;

				if (err && err.error && err.error.message) {
					message = err.error.message;
				}

				dispatch({
					type: SKIP_NEXT_QUESTION_FAILURE,
					error: true,
					payload: {
						error: message,
					},
				});
			});
	};
};

export const like = (userid) => {
	return (dispatch) => {
		dispatch({ type: LIKE });
		ProfileActions.like(userid);
	};
};

export const setAnsweringQuestion = (qid) => {
	return (dispatch) => {
		dispatch({
			type: SET_ANSWERING_QUESTION,
			payload: {
				qid,
			},
		});
	};
};

export const clearAnsweringQuestion = () => {
	return (dispatch) => {
		dispatch({ type: CLEAR_ANSWERING_QUESTION });

		// when we clear the answering question, reset the questions app to its
		// initial state
		dispatch({
			type: FETCH_QUESTION_SUCCESS,
			payload: {
				question: {
					...questionsInitialState.question,
					question: "",
				},
			},
		});
	};
};

export const addProfileActionListeners = () => {
	return (dispatch, getStore) => {
		subscribeToOkEvent(OKEVENT.Vote, ({ tuid }) => {
			const { profileQuestions } = getStore();

			if (profileQuestions.targetUser.userid === tuid) {
				dispatch({ type: LIKE_SUCCESS });
			}
		});

		subscribeToOkEvent(OKEVENT.VoteFailure, ({ tuid }) => {
			const { profileQuestions } = getStore();

			if (profileQuestions.targetUser.userid === tuid) {
				dispatch({
					type: LIKE_FAILURE,
					error: true,
					payload: {
						error: _str("There was a problem liking them!"),
					}
				});
			}
		});
	};
};

export const adminReanswer = (qid) => {
	return (dispatch) => {
		if (!Context.get("isStaff")) {
			return;
		}

		// 1 hour from now
		const expire = (new Date().getTime() / 1000) + 3600;

		$.ajax("/util/sookie_set", {
			data: {
				uid: window.CURRENTUSERID,
				slot: `question_flip_grace_${qid}`,
				expire,
				value: 1,
			},
			success: () => {
				dispatch({
					type: ADMIN_REANSWER,
					payload: {
						qid,
					},
				});
			},
		});
	};
};

export const removeProfileActionListeners = () => {
	return () => {
		unsubscribeFromOkEvent(OKEVENT.Vote);
		unsubscribeFromOkEvent(OKEVENT.VoteFailure);
	};
};

export const answerNextQuestion = () => {
	return { type: ANSWER_NEXT_QUESTION };
};



// WEBPACK FOOTER //
// ./src/ducks/profile_questions.js